import { Gender } from '../models/User';

// Matches inline gender markers like "(o|la)" or "(io|jela)". The two slots
// are masculine first, feminine second — the same order Croatian copywriters
// reach for when writing the slash form ("Odigrao/la"). Parens with nested
// parens or pipes don't match by construction so plain `(konvencije, festivali)`
// passes through untouched.
const TOKEN_RE = /\(([^()|]*)\|([^()|]*)\)/g;

/**
 * Resolves inline gender tokens in a template against `gender`.
 *
 * Authors write the masculine and feminine forms together with a shared stem:
 *   - "Odigra(o|la) prvu kampanju."  → "Odigrao..." or "Odigrala..."
 *   - "Vod(io|ila) prvu sesiju."     → "Vodio..."   or "Vodila..."
 *   - "Preživ(io|jela) Megadungeon." → "Preživio..." or "Preživjela..."
 *
 * Plain text outside tokens passes through unchanged. Tokens may appear any
 * number of times in the same template (each is resolved independently),
 * so a sentence with multiple gendered verbs only needs one helper call.
 *
 * Defaults to masculine because that's the project's chosen fallback for
 * unset preferences — see UserPreferences.preferredGender.
 */
export function applyGender(template: string, gender: Gender = Gender.Male): string {
    return template.replace(TOKEN_RE, (_, masc: string, fem: string) =>
        gender === Gender.Female ? fem : masc,
    );
}

/**
 * Explicit two-argument picker for cases where the masculine and feminine
 * forms don't share a clean stem (e.g. badge titles like "Junak" vs
 * "Junakinja"). Returns `fem` only when both are provided AND the user
 * prefers feminine; falls back to `masc` otherwise so callers can pass
 * `undefined` for the feminine override when no distinct form exists.
 */
export function pickGender(masc: string, fem: string | undefined, gender: Gender): string {
    if (gender === Gender.Female && fem != null) {
        return fem;
    }
    return masc;
}
